/**
 * Session Manager
 * Single-track unified conversation storage.
 *
 * IMPORTANT: This module does NOT directly depend on platform.
 * Storage implementation must be injected via store parameter.
 */

import { getUserId } from 'workers-common'

// Collection names
const NAMESPACE = 'sessions'
const COLLECTION_CONV_INDEX = 'conv_index'

// Configuration
export const CONFIG = {
  maxMessages: 500,
}

/**
 * Convert unified message format to LLM API format.
 * participant messages become [Name]: content as role: 'user'.
 * @param {Array} messages - Unified format messages
 * @param {Object} nameMap - participantId → displayName
 * @returns {Array} LLM API messages
 */
export function toLLMMessages(messages, nameMap = {}) {
  return messages.flatMap(msg => {
    if (msg.role === 'user') return [{ role: 'user', content: msg.content }]
    if (msg.role === 'assistant') return [{ role: 'assistant', content: msg.content }]
    if (msg.role === 'participant') {
      const name = nameMap[msg.participantId] || msg.participantId
      return [{ role: 'user', content: `[${name}]: ${msg.content}` }]
    }
    return []
  })
}

/**
 * Create Session Manager API
 * @param {Object} store - Store implementation
 * @returns {Object} API functions
 */
export function createSessionManager(store) {
  if (!store) throw new Error('Store implementation is required')

  // Per-user write serialization to prevent read-modify-write races
  const locks = new Map()
  const withLock = (userId, fn) => {
    const prev = locks.get(userId) || Promise.resolve()
    const next = prev.then(fn)
    const chain = next.catch(() => {})
    locks.set(userId, chain)
    chain.then(() => { if (locks.get(userId) === chain) locks.delete(userId) })
    return next
  }

  const requireAuth = (ctx) => {
    const userId = getUserId(ctx)
    if (!userId) throw new Error('User not authenticated')
    return userId
  }

  // --- Internal helpers ---
  const loadConvIndex = async (userId) => {
    return await store.get(NAMESPACE, userId, COLLECTION_CONV_INDEX) || []
  }

  const saveConvIndex = async (userId, index) => {
    await store.set(NAMESPACE, userId, COLLECTION_CONV_INDEX, index)
  }

  const loadConvMessages = async (userId, conversationId) => {
    return await store.get(NAMESPACE, userId, `conv:${conversationId}`) || []
  }

  const saveConvMessages = async (userId, conversationId, messages) => {
    await store.set(NAMESPACE, userId, `conv:${conversationId}`, messages)
  }

  const api = {
    // Internal methods (for testing)
    _internal: {
      loadConvIndex,
      saveConvIndex,
      loadConvMessages,
      saveConvMessages,
      toLLMMessages,
    },

    // --- Conversation index ---

    getConversationIndex: async (ctx) => {
      const userId = requireAuth(ctx)
      return loadConvIndex(userId)
    },

    createConversation: async (ctx, params) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        const { hostRoleId, title } = params
        const index = await loadConvIndex(userId)
        const entry = {
          id: globalThis.crypto.randomUUID(),
          hostRoleId,
          participantRoleIds: [],
          title: title || '',
          createdAt: Date.now(),
          updatedAt: Date.now(),
        }
        index.push(entry)
        await saveConvIndex(userId, index)
        return entry
      })
    },

    renameConversation: async (ctx, params) => {
      const userId = requireAuth(ctx)
      const { conversationId, title } = params
      return withLock(userId, async () => {
        const index = await loadConvIndex(userId)
        const entry = index.find(e => e.id === conversationId)
        if (!entry) throw new Error(`Conversation not found: ${conversationId}`)
        entry.title = title
        entry.updatedAt = Date.now()
        await saveConvIndex(userId, index)
        return entry
      })
    },

    deleteConversation: async (ctx, params) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        const { conversationId } = params
        const index = await loadConvIndex(userId)
        await saveConvIndex(userId, index.filter(e => e.id !== conversationId))
        await store.delete(NAMESPACE, userId, `conv:${conversationId}`)
        return { success: true }
      })
    },

    // --- Messages ---

    getMessages: async (ctx, params) => {
      const userId = requireAuth(ctx)
      const { conversationId, maxMessages = CONFIG.maxMessages } = params
      const messages = await loadConvMessages(userId, conversationId)
      return messages.length > maxMessages ? messages.slice(-maxMessages) : messages
    },

    getMessagesForLLM: async (ctx, params) => {
      const userId = requireAuth(ctx)
      const { conversationId, maxMessages = 100, nameMap = {} } = params
      const messages = await loadConvMessages(userId, conversationId)
      const trimmed = messages.length > maxMessages ? messages.slice(-maxMessages) : messages
      return toLLMMessages(trimmed, nameMap)
    },

    saveMessages: async (ctx, params) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        const { conversationId, messages: newMessages } = params
        const existing = await loadConvMessages(userId, conversationId)
        const now = Date.now()
        for (const msg of newMessages) {
          existing.push({ ...msg, timestamp: msg.timestamp || now })
        }
        const trimmed = existing.length > CONFIG.maxMessages
          ? existing.slice(-CONFIG.maxMessages)
          : existing
        await saveConvMessages(userId, conversationId, trimmed)

        // Update conv_index: updatedAt + auto-title from first user message
        const index = await loadConvIndex(userId)
        const entry = index.find(e => e.id === conversationId)
        if (entry) {
          entry.updatedAt = now
          if (!entry.title) {
            const firstUser = newMessages.find(m => m.role === 'user')
            if (firstUser) entry.title = firstUser.content.slice(0, 60)
          }
          await saveConvIndex(userId, index)
        }

        return { success: true }
      })
    },

    // --- Participants (per-conversation, stored in conv_index) ---

    getParticipants: async (ctx, params) => {
      const userId = requireAuth(ctx)
      const { conversationId } = params
      const index = await loadConvIndex(userId)
      const entry = index.find(e => e.id === conversationId)
      return entry?.participantRoleIds || []
    },

    setParticipants: async (ctx, params) => {
      const userId = requireAuth(ctx)
      const { conversationId, participantRoleIds } = params
      return withLock(userId, async () => {
        const index = await loadConvIndex(userId)
        const entry = index.find(e => e.id === conversationId)
        if (entry) {
          entry.participantRoleIds = participantRoleIds
          await saveConvIndex(userId, index)
        }
        return participantRoleIds
      })
    },

    // --- Housekeeping ---

    clear: async (ctx) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        const index = await loadConvIndex(userId)
        for (const entry of index) {
          await store.delete(NAMESPACE, userId, `conv:${entry.id}`)
        }
        await store.delete(NAMESPACE, userId, COLLECTION_CONV_INDEX)
        return { success: true }
      })
    },
  }

  const { _internal: _, ...sandbox } = api
  Object.defineProperty(api, 'sandbox', { value: sandbox, enumerable: false })
  return api
}
